import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select } from "./ui/select";
import API_BASE_URL, {
  createCheckoutSession,
  updateCheckoutShippingOptions,
} from "../services/api";
import { mountCheckoutForm } from "../utils/checkoutForm";
import { COUNTRY_NAME_TO_CODE } from "../utils/addressValidation";
import { useAuth } from "../context/AuthContext.jsx";
import logger from "../utils/logger";

// Initialize Stripe with a guard for DX
const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!publishableKey) {
  // Surface a clear error early in development if key is missing
  // eslint-disable-next-line no-console
  console.error(
    "Missing REACT_APP_STRIPE_PUBLISHABLE_KEY. Stripe will not initialize.",
  );
}
let stripePromise;
const getCheckoutStripe = () => {
  if (!publishableKey) return Promise.resolve(null);
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey).catch((error) => {
      stripePromise = null;
      throw error;
    });
  }
  return stripePromise;
};

const CheckoutPage = ({
  cartItems,
  handleUpdateQuantity,
  handleRemoveItem,
}) => {
  const [shippingAddress, setShippingAddress] = useState({
    street: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
  });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isAddressFormVisible, setIsAddressFormVisible] = useState(true);
  const [shippingOverride, setShippingOverride] = useState(null);
  const [printfulShippingCost, setPrintfulShippingCost] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState("");
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // US States array
  const usStates = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
    "District of Columbia",
  ];

  // Canadian Provinces array
  const caProvinces = [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Nova Scotia",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
  ];

  // Countries array
  const countries = [
    "Argentina",
    "Austria",
    "Belgium",
    "Canada",
    "Chile",
    "Colombia",
    "Denmark",
    "France",
    "Germany",
    "Ireland",
    "Italy",
    "Liechtenstein",
    "Luxembourg",
    "Monaco",
    "Netherlands",
    "Norway",
    "Peru",
    "Philippines",
    "Poland",
    "Portugal",
    "South Korea",
    "Spain",
    "Sweden",
    "Switzerland",
    "Thailand",
    "United Kingdom",
    "United States",
    "Vietnam",
  ];

  // Fetch user's saved addresses - Now depends on authLoading
  useEffect(() => {
    // Define the async function inside useEffect
    const fetchAddresses = async () => {
      logger.debug(
        "CheckoutPage: fetchAddresses running. AuthLoading:",
        authLoading,
        "User:",
        !!user,
      );
      // Only proceed if auth is loaded AND user is available
      if (!authLoading && user) {
        try {
          logger.debug(
            "CheckoutPage: Fetching user profile (already authenticated)...",
          );
          // No need to call getUserProfile again, user object from context has the profile
          const profile = user; // Use the user object from context
          logger.debug("CheckoutPage: User profile from context:", profile);

          if (profile && profile.addresses && profile.addresses.length > 0) {
            logger.debug("CheckoutPage: Addresses found:", profile.addresses);
            setSavedAddresses(profile.addresses);

            const defaultAddr = profile.addresses.find(
              (addr) => addr.isDefault,
            );
            logger.debug("CheckoutPage: Default address found:", defaultAddr);

            if (defaultAddr) {
              logger.debug(
                "CheckoutPage: Setting default address as selected.",
              );
              setShippingAddress({
                street: defaultAddr.street || "",
                addressLine2: defaultAddr.unit || "",
                city: defaultAddr.city || "",
                state: defaultAddr.state || "",
                zipCode: defaultAddr.zipCode || "",
                country: defaultAddr.country || "United States",
              });
              setSelectedAddress(defaultAddr._id);
              setIsAddressFormVisible(false);
              logger.debug("CheckoutPage: isAddressFormVisible set to false.");
            } else {
              logger.debug(
                "CheckoutPage: No default address found. Showing form.",
              );
              setIsAddressFormVisible(true);
              setSelectedAddress(null);
            }
          } else {
            logger.debug(
              "CheckoutPage: No addresses found in profile context.",
            );
            setIsAddressFormVisible(true);
            setSavedAddresses([]);
            setSelectedAddress(null);
            setShippingAddress({
              street: "",
              addressLine2: "",
              city: "",
              state: "",
              zipCode: "",
              country: "United States",
            });
          }
        } catch (err) {
          logger.error("CheckoutPage: Error processing profile context:", err);
          setIsAddressFormVisible(true);
          setSavedAddresses([]);
          setSelectedAddress(null);
          setShippingAddress({
            street: "",
            addressLine2: "",
            city: "",
            state: "",
            zipCode: "",
            country: "United States",
          });
        }
      } else if (!authLoading && !user) {
        // Auth loaded, but user is not logged in
        logger.debug("CheckoutPage: Auth loaded, user not authenticated.");
        setIsAddressFormVisible(true);
        setSavedAddresses([]);
        setSelectedAddress(null);
        setShippingAddress({
          street: "",
          addressLine2: "",
          city: "",
          state: "",
          zipCode: "",
          country: "United States",
        });
      } else if (authLoading) {
        logger.debug(
          "CheckoutPage: Waiting for authentication to finish loading...",
        );
        // Optionally set a loading state specific to address fetching here
      }
    };

    fetchAddresses();
    // Add authLoading to dependencies
  }, [user, authLoading]);

  const handleAddressChange = (e) => {
    const { name, value } = e.target;

    setShippingAddress((prev) => {
      if (name === "country") {
        let defaultState = "";
        if (value === "United States") {
          defaultState = usStates[0] || "";
        } else if (value === "Canada") {
          defaultState = caProvinces[0] || "";
        }
        return { ...prev, country: value, state: defaultState };
      }
      return { ...prev, [name]: value };
    });
  };

  // Determine region options and labels based on selected country
  const regionOptions =
    shippingAddress.country === "United States"
      ? usStates
      : shippingAddress.country === "Canada"
        ? caProvinces
        : [];

  const regionLabel =
    shippingAddress.country === "United States"
      ? "State"
      : shippingAddress.country === "Canada"
        ? "Province"
        : "Region";

  const postalLabel =
    shippingAddress.country === "United States" ? "ZIP Code" : "Postal Code";

  const handleSelectAddress = (address) => {
    // Ensure addressLine2 is treated as optional, default to empty string if null/undefined
    const addressToUse = {
      ...address,
      addressLine2: address.addressLine2 || "",
    };
    setSelectedAddress(address);
    setShippingAddress(addressToUse); // Update the shipping address state as well
    setIsAddressFormVisible(false);
    logger.info("CheckoutPage: Selected saved address.", { address });
  };

  const handleShowAddressForm = () => {
    setSelectedAddress(null);
    setIsAddressFormVisible(true);
  };

  const validateAddress = () => {
    if (selectedAddress) return true;

    const { street, city, state, zipCode, country } = shippingAddress;
    if (!street || !city || !state || !zipCode || !country) {
      toast.error("Please fill in all address fields");
      return false;
    }
    return true;
  };

  // Handle checkout button click
  const handleCheckout = async () => {
    /*
     * Always rely on the `shippingAddress` state when forwarding the address
     * to the backend.  The helper `handleSelectAddress` already synchronises
     * `shippingAddress` with any saved address picked by the shopper, so we
     * do not need to check `selectedAddress` separately here.  Using a single
     * source of truth avoids edge-cases where the selected address object is
     * forwarded without the expected field names (e.g. `street1` vs
     * `street`).  This, in turn, ensures that the backend can properly
     * pre-populate the Stripe Checkout form with the address chosen on the
     * cart page.
     */
    const addressToUse = shippingAddress;

    if (!validateAddress()) {
      toast.error("Please provide a complete shipping address.");
      return;
    }

    // Calculate Printful shipping first (if needed)
    try {
      const printfulItems = cartItems.filter((i) => i.printful);
      let pfCost = 0;
      if (printfulItems.length > 0) {
        const body = {
          recipient: {
            country_code: (() => {
              if (addressToUse.country_code) return addressToUse.country_code;
              if (addressToUse.country) {
                return (
                  COUNTRY_NAME_TO_CODE[addressToUse.country] ||
                  addressToUse.country.slice(0, 2).toUpperCase()
                );
              }
              return "US";
            })(),
            state_code: addressToUse.state_code || addressToUse.state,
            city: addressToUse.city,
            address1: addressToUse.street || addressToUse.street1 || "",
            zip: addressToUse.zipCode || addressToUse.postalCode,
          },
          items: printfulItems.map((p) => ({
            external_id: p.external_id,
            quantity: p.quantity,
          })),
        };

        const resp = await fetch(`${API_BASE_URL}/printful/shipping-rate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(body),
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.rates && data.rates.length > 0) {
            pfCost = data.rates[0].rate || data.rates[0].cost || 0;
          }
        }
      }
      setPrintfulShippingCost(pfCost);
    } catch (err) {
      logger.error("Failed to fetch Printful shipping rate", {
        error: err?.message,
      });
    }

    setShippingOverride(addressToUse);

    setIsEmbedded(true);
  };

  // State to toggle Stripe Checkout Form.
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutAttempt, setCheckoutAttempt] = useState(0);

  useEffect(() => {
    if (!isEmbedded) return;
    let cancelled = false;
    let checkoutForm;
    setCheckoutLoading(true);
    setCheckoutError("");

    (async () => {
      try {
        const stripe = await getCheckoutStripe();
        if (cancelled) return;
        if (!stripe) {
          throw new Error("Unable to load secure checkout. Please try again.");
        }
        const clientSecret = await createCheckoutSession({
          items: cartItems,
          shippingAddress: shippingOverride,
          couponCode: couponDiscount > 0 ? couponCode : null,
        });
        if (cancelled) return;
        checkoutForm = await mountCheckoutForm({
          stripe,
          clientSecret,
          container: "#checkout-container",
          updateShippingOptions: updateCheckoutShippingOptions,
          isCancelled: () => cancelled,
          onReady: () => {
            if (!cancelled) setCheckoutLoading(false);
          },
          onShippingUpdated: () => {
            if (!cancelled) setCheckoutError("");
          },
          onError: (error) => {
            if (cancelled) return;
            setCheckoutLoading(false);
            setCheckoutError(error.message || "Unable to update checkout.");
            logger.error("Checkout Form error", { error: error.message });
          },
        });
        if (cancelled) checkoutForm?.destroy();
      } catch (error) {
        if (cancelled) return;
        logger.error("Error initializing checkout", { error: error.message });
        setCheckoutLoading(false);
        setCheckoutError(error.message || "Unable to load secure checkout.");
      }
    })();
    return () => {
      cancelled = true;
      checkoutForm?.destroy();
    };
  }, [
    isEmbedded,
    cartItems,
    shippingOverride,
    couponCode,
    couponDiscount,
    checkoutAttempt,
  ]);

  // Render loading state while checking authentication
  const [isLoading, setIsLoading] = useState(true);

  // Check cart items
  // If the cart changes while the shopper is on the checkout page (for
  // instance when they remove an item from the navigation cart sheet), redirect
  // them back to the cart view so the interface accurately reflects the new
  // state and totals.  We only trigger the redirect when the number of items
  // in the cart decreases – this indicates a removal – or when the cart
  // becomes empty.
  const previousCartCountRef = useRef(cartItems.length);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    const previousCount = previousCartCountRef.current;
    const currentCount = cartItems.length;

    // Store current count for next effect run
    previousCartCountRef.current = currentCount;

    if (isFirstRenderRef.current) {
      // Skip redirect logic on initial mount
      isFirstRenderRef.current = false;
      return;
    }

    // Redirect only if the cart is now empty
    if (currentCount === 0) {
      toast.success("Your cart is empty. Redirecting to shop...");
      const timeoutId = setTimeout(() => {
        navigate("/#featured");
      }, 1500);
      return () => clearTimeout(timeoutId);
    }
  }, [cartItems, navigate]);

  // Check authentication status only once on mount
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      toast.error("Please log in to continue checkout");
      navigate("/login", { state: { from: "/checkout" } });
    }
    setIsLoading(false);
  }, [navigate, user, authLoading]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    setValidatingCoupon(true);
    setCouponError("");

    try {
      const response = await fetch(`${API_BASE_URL}/stripe/validate-coupon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ couponCode: couponCode.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setCouponDiscount(data.discount);
        toast.success(`Coupon applied! ${data.discount}% discount`);
      } else {
        setCouponError(data.message || "Invalid coupon code");
        setCouponDiscount(0);
      }
    } catch (error) {
      logger.error("Error validating coupon", { error: error?.message });
      setCouponError("Failed to validate coupon. Please try again.");
      setCouponDiscount(0);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode("");
    setCouponDiscount(0);
    setCouponError("");
    toast.success("Coupon removed");
  };

  const calculateSubtotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0,
    );
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = (subtotal * couponDiscount) / 100;
    return subtotal - discount;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Loading...
            </h2>
          </div>
        </div>
      </div>
    );
  }

  if (!localStorage.getItem("authToken")) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Please log in to continue checkout
            </h2>
          </div>
        </div>
      </div>
    );
  }

  // Keep loading and recovery controls visible while Stripe initializes.
  if (isEmbedded) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          {checkoutLoading && <p role="status">Loading secure checkout...</p>}
          {checkoutError && (
            <div className="mb-6 space-y-3">
              <p role="alert">{checkoutError}</p>
              <Button
                type="button"
                onClick={() => setCheckoutAttempt((attempt) => attempt + 1)}
              >
                Retry checkout
              </Button>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            className="mb-6"
            onClick={() => setIsEmbedded(false)}
          >
            Back to checkout details
          </Button>
          <div id="checkout-container" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center font-handwritten">
            Checkout
          </h1>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Order Summary
            </h2>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {cartItems.map((item) => {
                return (
                  <li
                    key={item.id}
                    className="flex items-center justify-between py-3"
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={
                          typeof item.image === "string" && item.image
                            ? item.image.startsWith("http")
                              ? item.image
                              : `${window.location.origin}${item.image}`
                            : "/images/products/ryldo/placeholder.jpg"
                        }
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-md"
                      />
                      <div>
                        <p className="text-gray-900 dark:text-white">
                          {item.name}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Label
                            htmlFor={`quantity-${item.cartItemId ?? item.id}`}
                            className="text-sm text-gray-500 dark:text-gray-400"
                          >
                            Qty:
                          </Label>
                          <Input
                            id={`quantity-${item.cartItemId ?? item.id}`}
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              handleUpdateQuantity(
                                item.cartItemId ?? item.id,
                                parseInt(e.target.value, 10) || 1,
                              )
                            }
                            className="w-16 text-black"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <p className="text-gray-900 dark:text-white">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          handleRemoveItem(item.cartItemId ?? item.id)
                        }
                        className="text-red-600 dark:text-red-400"
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <div className="flex justify-between">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  Subtotal
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  ${calculateSubtotal().toFixed(2)}
                </p>
              </div>
              {couponDiscount > 0 && (
                <div className="flex justify-between">
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Discount ({couponDiscount}%)
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    -$
                    {((calculateSubtotal() * couponDiscount) / 100).toFixed(2)}
                  </p>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  Estimated total
                </p>
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  ${calculateTotal().toFixed(2)}
                </p>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Shipping &amp; tax are calculated in the next step
              </p>
            </div>
          </div>

          {/* Coupon Code Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Coupon Code
            </h2>

            {couponDiscount > 0 ? (
              <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-5 h-5 text-green-600 dark:text-green-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-green-800 dark:text-green-200 font-medium">
                    Coupon &quot;{couponCode}&quot; applied ({couponDiscount}%
                    off)
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={removeCoupon}
                  className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) =>
                        setCouponCode(e.target.value.toUpperCase())
                      }
                      onKeyPress={(e) => e.key === "Enter" && validateCoupon()}
                      className="uppercase"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={validateCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    className="px-6"
                  >
                    {validatingCoupon ? "Validating..." : "Apply"}
                  </Button>
                </div>
                {couponError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {couponError}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Shipping Address
            </h2>

            {savedAddresses.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Saved Addresses
                </h3>
                <div className="space-y-3">
                  {savedAddresses.map((address) => (
                    <div
                      key={address._id} // Add unique key prop
                      className={`p-4 border rounded-md cursor-pointer ${
                        selectedAddress?._id === address._id
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                          : "border-gray-200 dark:border-gray-700"
                      }`}
                      onClick={() => handleSelectAddress(address)}
                    >
                      <p className="text-gray-900 dark:text-white">
                        {address.street}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        {address.city}, {address.state} {address.zipCode}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        {address.country}
                      </p>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleShowAddressForm}
                    className="mt-2"
                  >
                    Use a different address
                  </Button>
                </div>
              </div>
            )}

            {isAddressFormVisible && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="street">Street Address</Label>
                  <Input
                    id="street"
                    name="street"
                    value={shippingAddress.street}
                    onChange={handleAddressChange}
                    required
                  />
                </div>
                <div className="mt-4">
                  <Label htmlFor="addressLine2">
                    Address Line 2 (Optional)
                  </Label>
                  <Input
                    id="addressLine2"
                    name="addressLine2"
                    value={shippingAddress.addressLine2}
                    onChange={handleAddressChange}
                    placeholder="Apartment, suite, unit, building, floor, etc."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={shippingAddress.city}
                      onChange={handleAddressChange}
                      required
                    />
                  </div>
                  <div>
                    {regionOptions.length > 0 ? (
                      <>
                        <Label htmlFor="state">{regionLabel}</Label>
                        <Select
                          id="state"
                          name="state"
                          value={shippingAddress.state}
                          onChange={handleAddressChange}
                          required
                          className="text-gray-900 dark:text-gray-100"
                        >
                          <option
                            value=""
                            disabled
                            className="text-gray-500 dark:text-gray-400"
                          >
                            Select a {regionLabel.toLowerCase()}
                          </option>
                          {regionOptions.map((region) => (
                            <option
                              key={region}
                              value={region}
                              className="text-gray-900 dark:text-gray-100"
                            >
                              {region}
                            </option>
                          ))}
                        </Select>
                      </>
                    ) : (
                      <>
                        <Label htmlFor="state">{regionLabel}</Label>
                        <Input
                          id="state"
                          name="state"
                          value={shippingAddress.state}
                          onChange={handleAddressChange}
                          required
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="zipCode">{postalLabel}</Label>
                    <Input
                      id="zipCode"
                      name="zipCode"
                      value={shippingAddress.zipCode}
                      onChange={handleAddressChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Select
                      id="country"
                      name="country"
                      value={shippingAddress.country}
                      onChange={handleAddressChange}
                      required
                      className="text-gray-900 dark:text-gray-100"
                    >
                      {countries.map((country) => (
                        <option
                          key={country}
                          value={country}
                          className="text-gray-900 dark:text-gray-100"
                        >
                          {country}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleCheckout}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-handwritten"
            >
              Continue to Payment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

CheckoutPage.propTypes = {
  cartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      quantity: PropTypes.number.isRequired,
      image: PropTypes.string,
    }),
  ).isRequired,
  handleUpdateQuantity: PropTypes.func.isRequired,
  handleRemoveItem: PropTypes.func.isRequired,
};

export default CheckoutPage;
